import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface CopyButtonProps {
  text: string;
  size?: 'small' | 'medium' | 'large';
  color?: 'inherit' | 'default' | 'primary' | 'secondary';
  tooltip?: string;
}

export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  size = 'small',
  color = 'default',
  tooltip = 'Copy to clipboard',
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : tooltip}>
      <IconButton
        onClick={handleCopy}
        size={size}
        color={color}
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        aria-label={tooltip}
      >
        {copied ? (
          <CheckIcon fontSize={size === 'large' ? 'medium' : 'small'} color="success" />
        ) : (
          <ContentCopyIcon fontSize={size === 'large' ? 'medium' : 'small'} />
        )}
      </IconButton>
    </Tooltip>
  );
};
